import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ParentNav } from '../../components/shared/BottomNav';
import { updateFamilySettings, logOut, formatCurrency, deleteAccount } from '../../services/firebase';

const ParentSettings = () => {
  const { family, userDoc, authUser } = useAuth();
  const navigate = useNavigate();

  const [section, setSection] = useState(null); // 'allowance' | 'match' | null
  const [saving, setSaving] = useState(false);
  const [saved, setSavedMsg] = useState(null); // 'allowance' | 'match' | null
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);

  // Allowance form state
  const [amount, setAmount] = useState(family?.allowance?.amount || '');
  const [frequency, setFrequency] = useState(family?.allowance?.frequency || 'weekly');

  // Match form state
  const [matchEnabled, setMatchEnabled] = useState(family?.match?.enabled ?? true);
  const [matchType, setMatchType] = useState(family?.match?.type || 'percentage');
  const [matchValue, setMatchValue] = useState(family?.match?.value || '');
  const [monthlyCap, setMonthlyCap] = useState(family?.match?.monthlyCap || '');

  const inviteToken = family?.inviteToken;
  const inviteLink = inviteToken && family?.id
    ? `${process.env.REACT_APP_BASE_URL}/invite/${family.id}/${inviteToken}`
    : null;

  const studentFirst = family?.studentName?.split(' ')[0] || 'Student';

  const handleSaveAllowance = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await updateFamilySettings(family.id, {
        allowance: { ...family.allowance, amount: parseFloat(amount), frequency },
        match: family.match,
      });
      setSavedMsg('allowance');
      setTimeout(() => { setSavedMsg(null); setSection(null); }, 3000);
    } catch (err) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMatch = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await updateFamilySettings(family.id, {
        allowance: family.allowance,
        match: {
          enabled: matchEnabled,
          type: matchType,
          value: parseFloat(matchValue) || 0,
          monthlyCap: parseFloat(monthlyCap) || 0,
        },
      });
      setSavedMsg('match');
      setTimeout(() => { setSavedMsg(null); setSection(null); }, 3000);
    } catch (err) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    await logOut();
    navigate('/signin');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      await deleteAccount(family.id, authUser.uid, family.studentId);
      setDeleted(true);
      // Give listeners time to unmount cleanly before auth state changes
      setTimeout(async () => {
        await logOut();
        navigate('/signin');
      }, 2500);
    } catch (err) {
      setError('Failed to delete account. Please try again or contact support.');
      setDeleting(false);
    }
  };

  const copyInvite = () => {
    if (inviteLink) navigator.clipboard.writeText(inviteLink);
  };

  if (deleted) {
    return (
      <div className="screen screen--no-nav" style={{ display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>👋</div>
          <h2 style={{ marginBottom: 12 }}>Account deleted</h2>
          <p style={{ color: 'var(--color-text-2)', lineHeight: 1.6 }}>
            Your account and all data have been permanently removed. Signing you out now.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <ParentNav />
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
      </div>

      {saved === 'allowance' && (
        <div className="alert alert--success" style={{ marginBottom: 20 }}>
          ✓ Allowance updated — takes effect next Sunday
        </div>
      )}
      {saved === 'match' && (
        <div className="alert alert--success" style={{ marginBottom: 20 }}>
          ✓ Match updated — takes effect on next close-out
        </div>
      )}
      {error && (
        <div className="alert alert--error" style={{ marginBottom: 20 }}>{error}</div>
      )}

      <div className="stack stack-4">

        {/* Profile */}
        <SettingsSection title="Your account">
          <SettingsRow
            label="Name"
            value={userDoc?.displayName}
            icon="👤"
          />
          <SettingsRow
            label="Email"
            value={authUser?.email}
            icon="✉️"
          />
        </SettingsSection>

        {/* Allowance */}
        <SettingsSection title={`${studentFirst}'s allowance`}>
          {section === 'allowance' ? (
            <form onSubmit={handleSaveAllowance} className="stack stack-4"
              style={{ paddingTop: 8 }}>
              <div className="form-group">
                <label className="form-label">Amount</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">$</span>
                  <input className="form-input" type="number" min="1" step="0.01"
                    value={amount} onChange={e => setAmount(e.target.value)} required autoFocus />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Frequency</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  {['weekly', 'biweekly', 'monthly'].map(f => (
                    <button key={f} type="button" onClick={() => setFrequency(f)}
                      style={freqBtnStyle(frequency === f)}>
                      {f === 'biweekly' ? 'Bi-wkly' : f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn--secondary"
                  onClick={() => setSection(null)} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn--primary"
                  disabled={saving} style={{ flex: 1 }}>
                  {saving ? <span className="spinner spinner--white" style={{ width: 18, height: 18 }} /> : 'Save'}
                </button>
              </div>
            </form>
          ) : (
            <SettingsRow
              label="Allowance"
              value={`${formatCurrency(family?.allowance?.amount)} ${family?.allowance?.frequency}`}
              icon="💰"
              onEdit={() => setSection('allowance')}
            />
          )}
        </SettingsSection>

        {/* Match */}
        <SettingsSection title="Savings match">
          {section === 'match' ? (
            <form onSubmit={handleSaveMatch} className="stack stack-4" style={{ paddingTop: 8 }}>
              <div className="toggle-wrapper">
                <div>
                  <div style={{ fontWeight: 600 }}>Enable match</div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)' }}>
                    Reward {studentFirst} for saving
                  </div>
                </div>
                <label className="toggle">
                  <input type="checkbox" checked={matchEnabled}
                    onChange={e => setMatchEnabled(e.target.checked)} />
                  <span className="toggle-slider" />
                </label>
              </div>

              {matchEnabled && (
                <>
                  <div className="form-group">
                    <label className="form-label">Match type</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {['percentage', 'fixed'].map(t => (
                        <button key={t} type="button" onClick={() => setMatchType(t)}
                          style={freqBtnStyle(matchType === t)}>
                          {t === 'percentage' ? '% of savings' : '$ fixed'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      {matchType === 'percentage' ? 'Match %' : 'Match $ per $1 saved'}
                    </label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">{matchType === 'percentage' ? '%' : '$'}</span>
                      <input className="form-input" type="number" min="0" step="1"
                        value={matchValue} onChange={e => setMatchValue(e.target.value)} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Monthly cap (optional)</label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">$</span>
                      <input className="form-input" type="number" min="0" step="5"
                        placeholder="No cap"
                        value={monthlyCap} onChange={e => setMonthlyCap(e.target.value)} />
                    </div>
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn--secondary"
                  onClick={() => setSection(null)} style={{ flex: 1 }}>Cancel</button>
                <button type="submit" className="btn btn--primary"
                  disabled={saving} style={{ flex: 1 }}>
                  {saving ? <span className="spinner spinner--white" style={{ width: 18, height: 18 }} /> : 'Save'}
                </button>
              </div>
            </form>
          ) : (
            <SettingsRow
              label="Match"
              value={
                !family?.match?.enabled
                  ? 'Disabled'
                  : family?.match?.type === 'percentage'
                    ? `${family.match.value}% of savings`
                    : `$${family.match.value} per dollar saved`
              }
              icon="🎯"
              onEdit={() => setSection('match')}
            />
          )}
        </SettingsSection>

        {/* Invite */}
        {inviteLink && !family?.inviteAccepted && (
          <SettingsSection title="Student invite">
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-3)', marginBottom: 6 }}>
                Share this link with {studentFirst}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.75rem',
                wordBreak: 'break-all', color: 'var(--color-text-2)',
                background: 'var(--color-surface-2)', padding: 12,
                borderRadius: 'var(--radius-md)' }}>
                {inviteLink}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn--secondary" style={{ flex: 1 }} onClick={copyInvite}>
                Copy Link
              </button>
              {navigator.share && (
                <button className="btn btn--primary" style={{ flex: 1 }}
                  onClick={() => navigator.share({ url: inviteLink })}>
                  Share
                </button>
              )}
            </div>
          </SettingsSection>
        )}

        {/* Close account */}
        <SettingsSection title="Close account">
          {!showDeleteConfirm ? (
            <button
              className="btn btn--full"
              style={{ color: 'var(--color-error)', border: '1px solid var(--color-error)',
                background: 'none', fontSize: '0.875rem' }}
              onClick={() => setShowDeleteConfirm(true)}>
              Delete account
            </button>
          ) : (
            <div className="stack stack-4">
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-2)', lineHeight: 1.6 }}>
                This will permanently delete your account, {studentFirst}'s account, and all
                transaction history. This cannot be undone.
              </div>
              <div className="form-group">
                <label className="form-label">Type DELETE to confirm</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="DELETE"
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn--secondary"
                  onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                  style={{ flex: 1 }}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--danger"
                  disabled={deleteConfirmText !== 'DELETE' || deleting}
                  onClick={handleDeleteAccount}
                  style={{ flex: 1 }}>
                  {deleting
                    ? <span className="spinner spinner--white" style={{ width: 18, height: 18 }} />
                    : 'Delete everything'}
                </button>
              </div>
            </div>
          )}
        </SettingsSection>

        {/* Help & Support */}
        <SettingsSection title="Help & support">
          <a
            href="https://campuswallet.app/help"
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 12,
              padding: '6px 0', textDecoration: 'none' }}>
            <span style={{ fontSize: 18 }}>❓</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>Help Center</div>
              <div style={{ fontWeight: 600, marginTop: 2, fontSize: '0.9375rem',
                color: 'var(--color-primary)' }}>campuswallet.app/help</div>
            </div>
            <span style={{ color: 'var(--color-text-3)', fontSize: '0.875rem' }}>→</span>
          </a>
        </SettingsSection>

        {/* Sign out */}
        <button className="btn btn--ghost btn--full"
          style={{ color: 'var(--color-error)', marginTop: 8 }}
          onClick={handleSignOut}>
          Sign out
        </button>

        <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--color-text-3)' }}>
          Campus Wallet · Beta · v0.1
        </p>
      </div>
    </div>
  );
};

const SettingsSection = ({ title, children }) => (
  <div>
    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-text-3)',
      letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
      {title}
    </div>
    <div className="card">{children}</div>
  </div>
);

const SettingsRow = ({ label, value, icon, onEdit }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12,
    padding: '4px 0', justifyContent: 'space-between' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 18 }}>{icon}</span>
      <div>
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-3)' }}>{label}</div>
        <div style={{ fontWeight: 600, marginTop: 2 }}>{value}</div>
      </div>
    </div>
    {onEdit && (
      <button onClick={onEdit}
        style={{ background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--color-primary)', fontSize: '0.875rem', fontWeight: 600,
          fontFamily: 'var(--font-body)', padding: '4px 8px' }}>
        Edit
      </button>
    )}
  </div>
);

const freqBtnStyle = (active) => ({
  padding: '10px 8px', borderRadius: 'var(--radius-md)', border: '2px solid',
  borderColor: active ? 'var(--color-primary)' : 'var(--color-border)',
  background: active ? 'var(--color-primary-pale)' : 'white',
  color: active ? 'var(--color-primary)' : 'var(--color-text)',
  fontWeight: active ? 600 : 400, fontSize: '0.8125rem', cursor: 'pointer',
  fontFamily: 'var(--font-body)', transition: 'all 150ms ease',
});

export default ParentSettings;
